import {SagaIterator} from 'redux-saga';
import {call, put, select, cancel, putResolve, getContext} from 'redux-saga/effects';

import {
    AddressForFormType,
    createAddressForForm,
} from 'desktop/pages/checkout/utils/createAddressForForm/createAddressForForm';
import {getStatesSelector} from 'desktop/selectors/location/location';
import {AddressType, CurrentAddressType} from 'desktop/types/addressType';
import {apiConfig} from 'libs/api/api';
import {ActionType} from 'types/reducers';

import {addNewCustomerAddresses} from '../../../reducers/deliveryStep/customerAddresses/customerAddresses';
import {setEditStepError, setEditStepLoading} from '../../../reducers/editStep/editStep';
import {
    getAddressesSameness,
    getCustomerAddresses,
} from '../../../selectors/deliveryStep/deliveryStep';
import {ADDRESSES} from '../deliveryStepRootSaga/constants';
import {getSavedCustomerAddressesSaga} from '../deliveryStepSaga/deliveryStepSaga';

import {setFieldsAsValid} from '../setFieldsAsValid/setFieldsAsValid';

import {findUsingDeepComparison} from './utils/findUsingDeepComparison/findUsingDeepComparison';

/**
 * Disable loading for new customer address
 * @param {string} error - error
 */
export function* disableLoadingForNew(error: string): SagaIterator {
    yield put(setEditStepLoading(false));
    if (error) {
        yield put(setEditStepError(error));
    } else {
        const navigate = yield getContext('navigate');
        yield call(navigate, '/checkout/onepage');
    }
}

/**
 * Saga which check that we trying to save actually new address. Returns false if everything is OK
 * @param {AddressType} newAddress - New address provided by customer
 */
export function* cancelIfNewAddressAlreadyExist(newAddress: AddressType): SagaIterator {
    const {addresses} = yield select(getCustomerAddresses);
    const {email} = apiConfig.hostStore.getState().customer;
    const states = yield select(getStatesSelector, newAddress.country.value);
    const isAddressAlreadyExist = yield call<typeof findUsingDeepComparison>(
        findUsingDeepComparison,
        addresses,
        newAddress,
        {
            email,
            states,
        },
    );
    if (isAddressAlreadyExist) {
        yield call(
            disableLoadingForNew,
            'This address already exists, please fill a new one or click cancel',
        );
        yield cancel();
    }
}

/**
 * Saves validated customer address to address book
 * @param {AddressType} editedAddress - Received address provided by customer
 * @param {string} type - Type of address which will be updated
 */
export function* saveNewCustomerAddress(editedAddress: AddressType, type: string): SagaIterator {
    const isAddressesSame = yield select(getAddressesSameness);
    const {id: customerId} = apiConfig.hostStore.getState().customer;
    const formData = yield call(createAddressForForm, {
        ...editedAddress,
        customerId,
        isAddressesSame,
        region: editedAddress.state.value,
        regionID: editedAddress.regionID.value,
        type,
    } as AddressForFormType);
    yield putResolve(addNewCustomerAddresses(formData));
    yield call(getSavedCustomerAddressesSaga);
    yield call(disableLoadingForNew, '');
}

/**
 * Validates and updates received address and returns validation result
 * @param {AddressType} editedAddress - Address to validation and updating
 * @param {string} type - Type of received address
 * @return {SagaIterator<boolean>} Result of validation. Returns true if everything is OK
 */
export function* validateAndUpdateAddressSaga(
    editedAddress: AddressType,
    type: string,
): SagaIterator<boolean> {
    const updatedEditedForm = yield call(setFieldsAsValid, editedAddress);
    const saveEditedAddressAction = yield call(ADDRESSES[type].updater, updatedEditedForm);
    yield put(saveEditedAddressAction);
    return true;
}

/**
 * Adds new customer address to address book and store through validation
 * @param {CurrentAddressType} action - Action which contains address components
 */
export function* addNewCustomerAddressSaga({
    payload,
}: ActionType<CurrentAddressType>): SagaIterator {
    yield put(setEditStepLoading(true));
    const editedAddress = yield select(ADDRESSES[payload].selector);
    yield call(cancelIfNewAddressAlreadyExist, editedAddress);
    // Form validation before saving data
    const positiveValidationResult = yield call(
        validateAndUpdateAddressSaga,
        editedAddress,
        payload,
    );

    // If validation passed we prepare object for request and perform request
    if (positiveValidationResult) {
        yield call(saveNewCustomerAddress, editedAddress, payload);
    } else {
        yield call(disableLoadingForNew, '');
    }
}
